import { useState, useCallback, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  Upload as UploadIcon, 
  File, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  FileSpreadsheet,
  FileText,
  Trash2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import api from '../services/api'
import { extractPdfText } from '../services/pdfExtractor'
import { classifyDocument, type DocumentRecognitionResult, type RecognitionStep } from '../services/documentClassifier'
import { useWorkspace } from '../hooks/useWorkspace'
import { useLanguage } from '../hooks/useLanguage'
import TourGuide, { TourStep } from '../components/TourGuide'
import Modal, { ModalActions, ModalButton } from '../components/Modal'
import DocumentRecognition from '../components/DocumentRecognition'
import ImportStaging from '../components/ImportStaging'

interface UploadedFile {
  id: string
  filename: string
  file_type: string
  status: 'pending' | 'processing' | 'completed' | 'error' | 'queued'
  row_count: number
  upload_date: string
  error_message?: string
  warning_message?: string
}

interface UploadError {
  file: string
  message: string
  isDuplicate?: boolean
}

export default function Upload() {
  const { currentWorkspace } = useWorkspace()
  const { t, language } = useLanguage()
  const queryClient = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([])
  // Track files we've already notified about to avoid duplicate notifications
  const notifiedFilesRef = useRef<Set<string>>(new Set())
  const initialLoadRef = useRef(true)
  
  // Document Recognition Pipeline state
  const [recognitionResults, setRecognitionResults] = useState<Record<string, {
    result: DocumentRecognitionResult | null
    steps: RecognitionStep[]
    isProcessing: boolean
  }>>({})
  
  // Modal state for notifications
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    type: 'info' | 'success' | 'warning' | 'error'
    title: string
    message: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  })
  
  const showModal = (type: 'info' | 'success' | 'warning' | 'error', title: string, message: string) => {
    setModalState({ isOpen: true, type, title, message })
  }
  
  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }))
  }
  
  // Fetch uploaded files
  const { data: files, isLoading } = useQuery<UploadedFile[]>({
    queryKey: ['uploads'],
    queryFn: async () => {
      const response = await api.get('/upload/')
      return response.data
    },
    refetchInterval: (query) => {
      // Poll solo se ci sono file in elaborazione
      const data = query.state.data as UploadedFile[] | undefined
      const hasProcessing = data?.some(f => 
        f.status === 'processing' || f.status === 'pending' || f.status === 'queued'
      )
      if (hasProcessing) {
        return 3000 // 3 secondi durante elaborazione
      }
      return false // Stop polling quando completato
    },
    staleTime: 10000, // Considera i dati freschi per 10 secondi
  })

  // Monitora i cambi di stato dei file per notificare l'utente quando l'elaborazione finisce
  useEffect(() => {
    if (!files) return
    
    // Al primo caricamento, segna tutti i file già completati/errore come "già notificati"
    if (initialLoadRef.current) {
      initialLoadRef.current = false
      for (const file of files) {
        if (file.status === 'completed' || file.status === 'error') {
          notifiedFilesRef.current.add(file.id)
        }
      }
      return
    }
    
    for (const file of files) {
      // Notifica solo per file che hanno appena completato o avuto errore
      if (file.status === 'completed' && !notifiedFilesRef.current.has(file.id)) {
        notifiedFilesRef.current.add(file.id)
        
        if (file.row_count === 0) {
          showModal(
            'warning',
            language === 'it' ? 'Nessuna transazione trovata' : 'No transactions found',
            language === 'it'
              ? `Il file "${file.filename}" è stato elaborato ma non contiene transazioni riconoscibili. Verifica che sia un estratto conto valido con date, descrizioni e importi.`
              : `The file "${file.filename}" was processed but contains no recognizable transactions. Check that it is a valid bank statement with dates, descriptions and amounts.`
          )
        } else {
          showModal(
            'success',
            language === 'it' ? 'Elaborazione completata' : 'Processing complete',
            language === 'it'
              ? `Il file "${file.filename}" è stato elaborato con successo: ${file.row_count} transazion${file.row_count === 1 ? 'e trovata' : 'i trovate'}.`
              : `The file "${file.filename}" was processed successfully: ${file.row_count} transaction${file.row_count === 1 ? '' : 's'} found.`
          )
        }
        
        // Refresh delle query correlate
        queryClient.invalidateQueries({ queryKey: ['transactions'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['reports-dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['uncategorized-transactions'] })
      }
      
      if (file.status === 'error' && !notifiedFilesRef.current.has(file.id)) {
        notifiedFilesRef.current.add(file.id)
        showModal(
          'error',
          language === 'it' ? 'Errore elaborazione' : 'Processing error',
          language === 'it'
            ? `Errore nell'elaborazione di "${file.filename}": ${file.error_message || 'errore sconosciuto'}. Puoi riprovare con il pulsante "Riprova".`
            : `Error processing "${file.filename}": ${file.error_message || 'unknown error'}. You can retry with the "Retry" button.`
        )
      }
    }
  }, [files, language, queryClient])

  // ─── Document Recognition Pipeline ────────────────────────────────────────
  const runRecognitionPipeline = useCallback(async (file: File, pdfText?: string) => {
    const fileName = file.name
    
    // Initialize recognition state
    const initialSteps: RecognitionStep[] = [
      { id: 'detect', label: language === 'it' ? 'Rilevamento tipo file' : 'File type detection', status: 'pending' },
      { id: 'extract', label: language === 'it' ? 'Estrazione testo' : 'Text extraction', status: 'pending' },
      { id: 'classify', label: language === 'it' ? 'Classificazione documento' : 'Document classification', status: 'pending' },
      { id: 'fields', label: language === 'it' ? 'Estrazione campi' : 'Field extraction', status: 'pending' },
      { id: 'proposal', label: language === 'it' ? 'Proposta Prima Nota' : 'Journal entry proposal', status: 'pending' },
    ]
    
    setRecognitionResults(prev => ({
      ...prev,
      [fileName]: { result: null, steps: initialSteps, isProcessing: true }
    }))
    
    try {
      const result = await classifyDocument(file, pdfText, language, (updatedSteps) => {
        setRecognitionResults(prev => ({
          ...prev,
          [fileName]: { ...prev[fileName], steps: updatedSteps }
        }))
      })
      
      setRecognitionResults(prev => ({
        ...prev,
        [fileName]: { result, steps: result.steps, isProcessing: false }
      }))
    } catch (error) {
      console.error('[Recognition] Pipeline error:', error)
      setRecognitionResults(prev => ({
        ...prev,
        [fileName]: { ...prev[fileName], isProcessing: false }
      }))
    }
  }, [language])

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      
      let pdfTextForClassification: string | undefined
      
      // For PDF files, extract text client-side first
      if (file.name.toLowerCase().endsWith('.pdf')) {
        console.log('[Upload] Extracting PDF text client-side...')
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 5 // Show extraction starting
        }))
        
        try {
          const { text, pageCount } = await extractPdfText(file)
          console.log(`[Upload] Extracted ${text.length} chars from ${pageCount} pages`)
          pdfTextForClassification = text
          
          if (text.length > 0) {
            // Add extracted text to form data for worker to use
            formData.append('pdf_text', text)
          }
          if (text.length < 50) {
            // Very little text - likely scanned PDF, warn but don't block
            console.warn('[Upload] PDF has very little text - may be scanned/image-based')
          }
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: 30 // Extraction done, starting upload
          }))
        } catch (pdfError) {
          console.error('[Upload] PDF text extraction failed:', pdfError)
          // Show error and stop upload
          throw new Error(pdfError instanceof Error ? pdfError.message : 'Errore estrazione testo PDF')
        }
      }
      
      // Start Document Recognition Pipeline in parallel
      runRecognitionPipeline(file, pdfTextForClassification)
      
      const response = await api.post('/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) {
            // Start from 30% if PDF (extraction done), otherwise from 0
            const baseProgress = file.name.toLowerCase().endsWith('.pdf') ? 30 : 0
            const uploadProgress = Math.round((e.loaded * 70) / e.total!)
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: baseProgress + uploadProgress
            }))
          }
        }
      })
      return { ...response.data, fileName: file.name }
    },
    onSuccess: (_data, file) => {
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
      
      // Il file è in coda per l'elaborazione asincrona - non mostrare risultati ancora
      // Le notifiche finali vengono gestite dal useEffect che monitora i cambi di stato
      
      // Rimuovi dalla progress
      setUploadProgress(prev => {
        const updated = { ...prev }
        delete updated[file.name]
        return updated
      })
    },
    onError: (error: Error, file) => {
      setUploadErrors(prev => [...prev, {
        file: file.name,
        message: error.message || 'Errore durante il caricamento',
        isDuplicate: false
      }])
      
      // Rimuovi dalla progress
      setUploadProgress(prev => {
        const updated = { ...prev }
        delete updated[file.name]
        return updated
      })
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      await api.delete(`/upload/${fileId}`)
    },
    onSuccess: () => {
      // Invalida tutte le query correlate per azzerare le statistiche
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['reports-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['uncategorized-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['categories', currentWorkspace?.id] })
    }
  })

  // Retry single upload mutation
  const retryMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const response = await api.post(`/upload/${fileId}/retry`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
    },
    onError: (error: Error) => {
      setUploadErrors(prev => [...prev, {
        file: 'Riprova',
        message: error.message || 'Errore durante il riprocessamento'
      }])
    }
  })

  // ─── Smart Upload: Document Parser + Staging ─────────────────────────────
  // All parsing is done server-side by the Python document-parser service.
  // Client only uploads the raw file — no client-side text extraction needed.
  const smartUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      setUploadProgress(prev => ({ ...prev, [file.name]: 5 }))

      // Start recognition pipeline UI in parallel (visual feedback only)
      runRecognitionPipeline(file)

      const response = await api.post('/document-parser/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 min for large files + OCR
        onUploadProgress: (e) => {
          if (e.total) {
            const progress = Math.round((e.loaded * 95) / e.total!)
            setUploadProgress(prev => ({ ...prev, [file.name]: 5 + progress }))
          }
        }
      })
      return { ...response.data, fileName: file.name }
    },
    onSuccess: (_data, file) => {
      // Staging record created — refresh staging list
      queryClient.invalidateQueries({ queryKey: ['staging-list'] })
      setUploadProgress(prev => {
        const updated = { ...prev }
        delete updated[file.name]
        return updated
      })
      const txCount = _data?.transaction_count || 0
      const confidence = _data?.overall_confidence ? Math.round(_data.overall_confidence * 100) : 0
      const docType = _data?.document_type || 'unknown'
      showModal(
        txCount > 0 ? 'success' : 'warning',
        language === 'it' ? 'Documento analizzato' : 'Document analyzed',
        language === 'it'
          ? txCount > 0
            ? `"${file.name}" analizzato: ${txCount} transazion${txCount === 1 ? 'e' : 'i'}, tipo: ${docType}, confidenza: ${confidence}%. Verifica l'anteprima prima di confermare.`
            : `"${file.name}" analizzato ma nessuna transazione trovata. Tipo rilevato: ${docType}. Verifica che il file sia un documento finanziario valido.`
          : txCount > 0
            ? `"${file.name}" analyzed: ${txCount} transaction${txCount === 1 ? '' : 's'}, type: ${docType}, confidence: ${confidence}%. Review the preview before confirming.`
            : `"${file.name}" analyzed but no transactions found. Detected type: ${docType}. Verify this is a valid financial document.`
      )
    },
    onError: (error: Error, file) => {
      // Fallback to legacy upload
      console.warn('[SmartUpload] Parser failed, falling back to legacy upload:', error.message)
      uploadMutation.mutate(file)
      setUploadProgress(prev => {
        const updated = { ...prev }
        delete updated[file.name]
        return updated
      })
    }
  })

  // Reprocess mutation
  const reprocessMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/reprocess-uploads')
      return response.data
    },
    onSuccess: (data) => {
      console.log('Reprocess result:', data)
      // Invalida tutte le query per ricaricare i dati
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['reports-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['uncategorized-transactions'] })
      showModal('success', 'Elaborazione completata', data.message || 'Tutti i file sono stati ri-elaborati con successo.')
    },
    onError: (error: Error) => {
      showModal('error', 'Errore elaborazione', error.message || 'Si è verificato un errore durante la ri-elaborazione.')
    }
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(file => {
      smartUploadMutation.mutate(file)
    })
  }, [smartUploadMutation])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true, // Abilita upload multiplo
    accept: {
      'text/csv': ['.csv'],
      'text/tab-separated-values': ['.tsv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf'],
      'text/xml': ['.xml'],
      'application/xml': ['.xml'],
      'application/json': ['.json'],
      'text/plain': ['.txt', '.ofx', '.qif', '.mt940'],
    },
    maxSize: 50 * 1024 * 1024 // 50MB
  })

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-5 h-5 text-green-600" />
      default:
        return <File className="w-5 h-5 text-blue-500" />
    }
  }

  // Rotating status messages for processing state
  const processingMessages = language === 'it'
    ? ['In coda...', 'Analizzo il documento...', 'Estraggo i dati...', 'Classifico le voci...', 'Quasi pronto...']
    : ['Queued...', 'Analyzing document...', 'Extracting data...', 'Classifying entries...', 'Almost ready...']

  const ProcessingBadge = () => {
    const [msgIndex, setMsgIndex] = useState(0)
    useEffect(() => {
      const interval = setInterval(() => {
        setMsgIndex(prev => (prev + 1) % processingMessages.length)
      }, 2500)
      return () => clearInterval(interval)
    }, [])
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/50 dark:to-indigo-900/50 text-blue-700 dark:text-blue-300 rounded-full shadow-sm border border-blue-200 dark:border-blue-800 animate-pulse min-w-[160px] justify-center">
        <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
        <span className="transition-all duration-300">{processingMessages[msgIndex]}</span>
      </span>
    )
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="badge bg-green-100 text-green-800 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            {t.upload.completed}
          </span>
        )
      case 'processing':
      case 'queued':
        return <ProcessingBadge />
      case 'error':
        return (
          <span className="badge bg-red-100 text-red-800 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            {t.common.error}
          </span>
        )
      default:
        return (
          <span className="badge bg-gray-100 text-gray-800">
            {t.upload.waiting}
          </span>
        )
    }
  }

  // Tour steps for Upload
  const tourSteps: TourStep[] = language === 'it' ? [
    {
      title: 'Carica i tuoi Estratti Conto! 📁',
      content: 'Qui puoi importare gli estratti conto dalla tua banca. Supportiamo CSV, Excel e PDF.',
      position: 'center'
    },
    {
      target: '[data-tour="dropzone"]',
      title: 'Area di Upload',
      content: 'Trascina i file qui o clicca per selezionarli. Puoi caricare più file contemporaneamente.',
      position: 'bottom'
    },
    {
      target: '[data-tour="file-list"]',
      title: 'File Caricati',
      content: 'Qui vedrai tutti i file importati con il numero di transazioni estratte. Puoi eliminarli se necessario.',
      position: 'top'
    },
    {
      target: '[data-tour="tips"]',
      title: 'Suggerimenti',
      content: 'Segui questi consigli per ottenere i migliori risultati dall\'importazione.',
      position: 'top'
    }
  ] : [
    {
      title: 'Upload Your Bank Statements! 📁',
      content: 'Here you can import bank statements. We support CSV, Excel and PDF.',
      position: 'center'
    },
    {
      target: '[data-tour="dropzone"]',
      title: 'Upload Area',
      content: 'Drag files here or click to select them. You can upload multiple files at once.',
      position: 'bottom'
    },
    {
      target: '[data-tour="file-list"]',
      title: 'Uploaded Files',
      content: 'Here you will see all imported files with the number of extracted transactions. You can delete them if needed.',
      position: 'top'
    },
    {
      target: '[data-tour="tips"]',
      title: 'Tips',
      content: 'Follow these tips to get the best results from import.',
      position: 'top'
    }
  ]

  return (
    <>
      <TourGuide tourId="upload" steps={tourSteps} />
      <div className="space-y-6 pt-1">
        {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t.nav.uploadFile}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            {t.upload.supportedFormats}
          </p>
        </div>
        
        {/* Reprocess Button */}
        {files && files.length > 0 && (
          <button
            onClick={() => reprocessMutation.mutate()}
            disabled={reprocessMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 dark:bg-slate-100 dark:text-slate-900 rounded-lg hover:bg-slate-900 dark:hover:bg-white transition-colors shadow-sm disabled:opacity-50"
          >
            {reprocessMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.upload.processing}
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-4 h-4" />
                {t.upload.reprocess}
              </>
            )}
          </button>
        )}
      </div>

      {/* Upload Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Dropzone */}
        <div
          data-tour="dropzone"
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-colors
            ${isDragActive 
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
              : 'border-gray-300 dark:border-slate-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-slate-800'
            }
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center">
            <div className={`
              p-4 rounded-full mb-4
              ${isDragActive ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-gray-100 dark:bg-slate-700'}
            `}>
              <UploadIcon className={`
                w-8 h-8
                ${isDragActive ? 'text-primary-600' : 'text-gray-400'}
              `} />
            </div>
            
            {isDragActive ? (
              <p className="text-sm font-medium text-primary-600">
                Rilascia il file qui...
              </p>
            ) : (
              <>
                <p className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Trascina o seleziona file
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  CSV, XLS, XLSX, PDF, XML, OFX, QIF, TSV (max 50MB)
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 max-w-sm">
                  {language === 'it'
                    ? 'Estratti conto · Fatture · Situazioni contabili · Piani dei conti · F24 · Bilanci · CU · Ricevute'
                    : 'Bank statements · Invoices · Trial balances · Charts of accounts · Tax forms · Financial statements · Receipts'}
                </p>
              </>
            )}
          </div>
        </div>

      </div>

      {/* Upload Progress */}
      {Object.entries(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([name, progress]) => (
            <div key={name} className="card bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {progress < 100 ? 'Caricamento...' : 'Elaborazione...'} {name}
                  </p>
                  {progress >= 100 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                      L'AI sta analizzando i dati. I risultati saranno disponibili a breve...
                    </p>
                  )}
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 mt-1">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {progress < 100 ? `${progress}%` : 'Elaborazione...'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Errors / Duplicates */}
      {uploadErrors.length > 0 && (
        <div className="space-y-2">
          {uploadErrors.map((error, idx) => (
            <div 
              key={idx} 
              className={`card flex items-start gap-3 ${
                error.isDuplicate ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'
              }`}
            >
              <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                error.isDuplicate ? 'text-yellow-600' : 'text-red-600'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  error.isDuplicate ? 'text-yellow-800' : 'text-red-800'
                }`}>
                  {error.isDuplicate ? 'File duplicato' : 'Errore caricamento'}: {error.file}
                </p>
                <p className={`text-xs mt-1 ${
                  error.isDuplicate ? 'text-yellow-700' : 'text-red-700'
                }`}>
                  {error.message}
                </p>
              </div>
              <button
                onClick={() => setUploadErrors(prev => prev.filter((_, i) => i !== idx))}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Document Recognition Pipeline Results */}
      {Object.keys(recognitionResults).length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M9 15l2 2 4-4" />
              </svg>
              {language === 'it' ? 'Riconoscimento Documenti' : 'Document Recognition'}
            </h3>
            {Object.values(recognitionResults).every(r => !r.isProcessing) && (
              <button
                onClick={() => setRecognitionResults({})}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {language === 'it' ? 'Chiudi tutti' : 'Close all'}
              </button>
            )}
          </div>

          {/* Banner elaborazione in corso */}
          {Object.values(recognitionResults).some(r => r.isProcessing) && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700">
              <Loader2 className="w-4 h-4 text-orange-500 animate-spin flex-shrink-0" />
              <p className="text-xs font-medium text-orange-700 dark:text-orange-300">
                {language === 'it' ? 'Elaborazione file in corso — attendere il completamento...' : 'Processing file — please wait...'}
              </p>
            </div>
          )}

          {Object.entries(recognitionResults).map(([fileName, data]) => (
            <DocumentRecognition
              key={fileName}
              fileName={fileName}
              result={data.result}
              steps={data.steps}
              isProcessing={data.isProcessing}
            />
          ))}
        </div>
      )}

      {/* Import Staging Preview */}
      <ImportStaging />

      {/* Uploaded Files */}
      <div data-tour="file-list" className="card">
        <div className="card-header">
          <h3 className="card-title">File Caricati</h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : files && files.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {files.map((file) => (
              <div key={file.id} className="py-4">
                <div className="flex items-center gap-4">
                  {getFileIcon(file.file_type)}
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(file.upload_date).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {file.status === 'completed' && (
                        <> • {file.row_count} transazioni</>
                      )}
                    </p>
                    {file.error_message && (
                      <p className="text-xs text-red-600 mt-1">
                        {file.error_message}
                      </p>
                    )}
                    {file.warning_message && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        ⚠ {file.warning_message}
                      </p>
                    )}
                  </div>

                  {getStatusBadge(file.status)}

                  {/* Retry button for error status */}
                  {file.status === 'error' && (
                    <button
                      onClick={() => retryMutation.mutate(file.id)}
                      disabled={retryMutation.isPending}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50"
                      title="Riprova"
                    >
                      {retryMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => deleteMutation.mutate(file.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Skeleton extraction preview for queued/processing files */}
                {(file.status === 'queued' || file.status === 'processing') && (
                  <div className="mt-3 ml-9 rounded-xl border border-blue-200 dark:border-blue-800/60 bg-gradient-to-br from-blue-50/80 to-indigo-50/60 dark:from-blue-950/30 dark:to-indigo-950/20 p-4 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-3">
                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                      <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                        {language === 'it' ? 'Campi in estrazione...' : 'Extracting fields...'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[language === 'it' ? 'Tipo documento' : 'Document type',
                        language === 'it' ? 'Data' : 'Date',
                        language === 'it' ? 'Importo' : 'Amount',
                        language === 'it' ? 'Controparte' : 'Counterparty',
                        language === 'it' ? 'Categoria' : 'Category',
                        language === 'it' ? 'IVA' : 'VAT'
                      ].map((label, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">{label}</p>
                            <div className={`h-4 mt-0.5 rounded bg-gradient-to-r from-blue-200/60 via-blue-100/40 to-blue-200/60 dark:from-blue-800/40 dark:via-blue-700/20 dark:to-blue-800/40 animate-shimmer`}
                              style={{ width: `${55 + (i * 11) % 40}%`, animationDelay: `${i * 200}ms` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <File className="w-12 h-12 text-slate-400 dark:text-slate-500 mb-4" />
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Nessun file caricato
            </p>
            <p className="text-sm text-slate-400 max-w-[360px] text-center">
              Carica il tuo primo estratto conto per iniziare
            </p>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div data-tour="tips" className="card bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
        <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-3">
          {language === 'it' ? 'Documenti supportati' : 'Supported documents'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              {language === 'it' ? 'Documenti bancari' : 'Banking documents'}
            </p>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li>• {language === 'it' ? 'Estratti conto (CSV, XLS, PDF)' : 'Bank statements (CSV, XLS, PDF)'}</li>
              <li>• {language === 'it' ? 'Movimenti bancari (qualsiasi banca italiana)' : 'Bank transactions (any Italian bank)'}</li>
              <li>• {language === 'it' ? 'OFX, QIF, MT940 (formati bancari standard)' : 'OFX, QIF, MT940 (standard banking formats)'}</li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              {language === 'it' ? 'Documenti contabili' : 'Accounting documents'}
            </p>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li>• {language === 'it' ? 'Fatture emesse e ricevute (PDF, XML)' : 'Issued and received invoices (PDF, XML)'}</li>
              <li>• {language === 'it' ? 'Piano dei conti (CSV, TSV, XLS)' : 'Chart of accounts (CSV, TSV, XLS)'}</li>
              <li>• {language === 'it' ? 'Situazioni contabili e bilanci (PDF, XLS)' : 'Trial balances and financial statements (PDF, XLS)'}</li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              {language === 'it' ? 'Documenti fiscali' : 'Tax documents'}
            </p>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li>• {language === 'it' ? 'F24, CU, Modello 730 (PDF)' : 'F24, CU, Form 730 (PDF)'}</li>
              <li>• {language === 'it' ? 'Ricevute e scontrini (PDF, immagini)' : 'Receipts (PDF, images)'}</li>
              <li>• {language === 'it' ? 'XBRL (bilanci depositati)' : 'XBRL (filed financial statements)'}</li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
              {language === 'it' ? 'Altro' : 'Other'}
            </p>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li>• {language === 'it' ? 'Buste paga / Cedolini (PDF)' : 'Payslips (PDF)'}</li>
              <li>• {language === 'it' ? 'Contratti (PDF)' : 'Contracts (PDF)'}</li>
              <li>• {language === 'it' ? 'I file duplicati vengono ignorati automaticamente' : 'Duplicate files are automatically skipped'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    
    {/* Notification Modal */}
    <Modal
      isOpen={modalState.isOpen}
      onClose={closeModal}
      title={modalState.title}
      type={modalState.type}
    >
      <p>{modalState.message}</p>
      <ModalActions>
        <ModalButton onClick={closeModal} variant="primary">
          Ho capito
        </ModalButton>
      </ModalActions>
    </Modal>
    </>
  )
}
